/**
 * @module utils/formatters
 * @description Utility functions for formatting data (file sizes, dates, etc.)
 */

/**
 * Format file size from bytes to human-readable format
 * Converts bytes to KB, MB, or GB as appropriate
 *
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size string (e.g., "1.5 MB", "245 KB")
 *
 * @example
 * formatFileSize(1024);           // "1 KB"
 * formatFileSize(1536000);        // "1.46 MB"
 * formatFileSize(2147483648);     // "2 GB"
 */
export function formatFileSize(bytes) {
    if (!bytes) return 'Unknown';

    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100  } ${  sizes[i]}`;
}

/**
 * Format date string to localized date and time (UK format)
 * Converts ISO date string to readable format using UK locale (DD/MM/YYYY, 24-hour time)
 *
 * @param {string} dateString - ISO date string or any valid date string
 * @returns {string} Formatted date and time string (e.g., "15/01/2024, 15:30:00")
 *
 * @example
 * formatDate('2024-01-15T15:30:00Z');  // "15/01/2024, 15:30:00"
 * formatDate('2024-11-08');            // "08/11/2024, 00:00:00"
 */
export function formatDate(dateString) {
    if (!dateString) return 'Unknown';

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
        return 'Invalid Date';
    }

    return `${date.toLocaleDateString('en-GB')  }, ${  date.toLocaleTimeString('en-GB', { hour12: false })}`;
}

/**
 * Format date to short format (date only, no time) - UK format
 *
 * @param {string} dateString - ISO date string or any valid date string
 * @returns {string} Formatted date string (e.g., "15/01/2024")
 *
 * @example
 * formatDateShort('2024-01-15T15:30:00Z');  // "15/01/2024"
 */
export function formatDateShort(dateString) {
    if (!dateString) return 'Unknown';

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
        return 'Invalid Date';
    }

    return date.toLocaleDateString('en-GB');
}

/**
 * Format timestamp to relative time (e.g., "2 hours ago", "3 days ago")
 *
 * @param {string|Date} timestamp - Date string or Date object
 * @returns {string} Relative time string
 *
 * @example
 * formatRelativeTime(new Date(Date.now() - 3600000));  // "1 hour ago"
 * formatRelativeTime(new Date(Date.now() - 86400000)); // "1 day ago"
 */
export function formatRelativeTime(timestamp) {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;

    return formatDateShort(timestamp);
}
